import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe, UpperCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { WaterBody } from '../models/water-body';
import { WaterbodiesService } from './waterbodies.service';
import { AdvisoryTabService, AdvisoryTab } from '../advisory-tab.service';
import { WaterBodySelectionService } from '../waterbody-selection.service';
import { APP_CONFIG, AppConfig } from '../models/app-config';
import { getWaterClassInfo, WATER_CLASSES } from '../models/water-class';

@Component({
  selector: 'app-waterbodies',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, UpperCasePipe],
  templateUrl: './waterbodies.component.html',
  styleUrls: ['./waterbodies.component.css'],
})
export class WaterbodiesComponent implements OnInit {
  activeTab: AdvisoryTab = 'wq';
  waterBodies: WaterBody[] = [];
  keyword = '';
  onAdvisoryOnly = false;
  isLoading = false;
  errorMessage: string | null = null;
  activeInfoId: string | number | null = null;

  readonly MAX_HOURS: Record<AdvisoryTab, number> = { cso: 6, wq: 200 };

  readonly CSO_RANGES = [
    { label: 'No discharge',  color: 'rgba(0, 0, 255, 0.5)' },
    { label: '≤5 MG',         color: '#006400' },
    { label: '≤20 MG',        color: '#90EE90' },
    { label: '≤50 MG',        color: '#FFFF00' },
    { label: '≤200 MG',       color: '#FFD700' },
    { label: '≤500 MG',       color: '#FFA500' },
    { label: '≤1,000 MG',     color: '#FFB6C1' },
    { label: '>1,000 MG',     color: '#8B0000' },
  ];

  constructor(
    private service: WaterbodiesService,
    private tabService: AdvisoryTabService,
    private selectionService: WaterBodySelectionService,
    @Inject(APP_CONFIG) public config: AppConfig
  ) {}

  ngOnInit(): void {
    if (this.config.showAdvisories.length > 0) {
      const initialTab = this.config.showAdvisories.includes('wq') ? 'wq' : this.config.showAdvisories[0];
      this.activeTab = initialTab as AdvisoryTab;
      this.tabService.setTab(this.activeTab);
      this.load(this.activeTab);
    }
  }

  selectTab(tab: AdvisoryTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.tabService.setTab(tab);
    this.load(tab);
  }

  showTab(tab: AdvisoryTab): boolean {
    return this.config.showAdvisories.includes(tab);
  }

  reset(): void {
    this.keyword = '';
    this.onAdvisoryOnly = false;
    this.selectionService.selectWaterBody(null);
  }

  get filteredWaterBodies(): WaterBody[] {
    const kw = this.keyword.trim().toLowerCase();
    return this.waterBodies
      .filter(wb => !kw || wb.name.toLowerCase().includes(kw))
      .filter(wb => !this.onAdvisoryOnly || wb.hasAdvisory)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  csoBarColor(wb: WaterBody): string {
    const v = wb.advisory?.volume ?? 0;
    if (v === 0)   return 'rgba(0, 0, 255, 0.5)';
    if (v <= 5)    return '#006400';
    if (v <= 20)   return '#90EE90';
    if (v <= 50)   return '#FFFF00';
    if (v <= 200)  return '#FFD700';
    if (v <= 500)  return '#FFA500';
    if (v <= 1000) return '#FFB6C1';
    return '#8B0000';
  }

  progressPercent(wb: WaterBody): number {
    if (!wb.hasAdvisory || !wb.advisory) return 0;
    const max = this.MAX_HOURS[this.activeTab] ?? 48;
    return (Math.min(wb.advisory.hours, max) / max) * 100;
  }

  selectWaterBody(wb: WaterBody): void {
    this.selectionService.selectWaterBody(wb);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleClassInfo(wb: WaterBody, event: MouseEvent): void {
    event.stopPropagation();
    this.activeInfoId = this.activeInfoId === wb.id ? null : wb.id;
  }

  getWaterClassInfo(classCode?: string) {
    return getWaterClassInfo(classCode);
  }

  private load(tab: AdvisoryTab): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.waterBodies = [];
    this.service.getWaterBodies(tab).pipe(
      catchError(() => {
        this.errorMessage = 'Failed to load water bodies.';
        return of([]);
      })
    ).subscribe(data => {
      this.waterBodies = data;
      this.isLoading = false;
    });
  }
}
